import { useEffect, useMemo, useState, useId } from 'react'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import type { AvailableDayInfo, DaySlot, Ticket } from '../domain/types'

const dateLabel = (value: string): string => {
  const [year, month, day] = value.split('-').map(Number)
  const d = new Date(year, month - 1, day)
  return d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })
}

const timeLabel = (value: string): string => {
  const match = value.match(/T(\d{2}:\d{2})/)
  if (match) {return match[1]}
  const compact = value.match(/^(\d{2}:\d{2})/)
  return compact?.[1] ?? value
}

const sessionLabel = (slot: DaySlot): string => {
  if (slot.start && slot.end) {return `${timeLabel(slot.start)}-${timeLabel(slot.end)}`}
  return slot.label
}

export function RescheduleModal({
  ticket,
  submitting = false,
  getAvailableDays,
  getDaySlots,
  onClose,
  onConfirm,
}: {
  ticket: Ticket
  submitting?: boolean
  getAvailableDays: (serviceConfigId: number) => Promise<AvailableDayInfo[]>
  getDaySlots: (serviceConfigId: number, date: string) => Promise<DaySlot[]>
  onClose: () => void
  onConfirm: (sessionId: number) => void
}) {
  const titleId = useId()
  const serviceConfigId = ticket.serviceConfigId
  const [days, setDays] = useState<AvailableDayInfo[] | null>(null)
  const [date, setDate] = useState('')
  const [slots, setSlots] = useState<DaySlot[] | null>(null)
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const availableSlots = useMemo(() => (slots ?? []).filter((slot) => slot.available), [slots])
  const { futureSlots, pastSlots } = useMemo(() => {
    const now = Date.now()
    const future: DaySlot[] = []
    const past: DaySlot[] = []
    for (const slot of availableSlots) {
      const s = slot.start
      const hasTz = /[Zz]|[+-]\d{2}:\d{2}$/.test(s)
      // Slot times vêm do backend em America/Maceio (UTC-3, sem DST).
      // Sem timezone explícito, new Date(s) interpreta como timezone local
      // do browser, corrompendo a comparação com Date.now() (UTC).
      const dateStr = hasTz ? s : s + '-03:00'
      const startMs = new Date(dateStr).getTime()
      if (!Number.isNaN(startMs) && startMs <= now) {past.push(slot)}
      else {future.push(slot)}
    }
    return { futureSlots: future, pastSlots: past }
  }, [availableSlots])

  useEffect(() => {
    if (!serviceConfigId) {
      setDays([])
      setError('Este ticket não tem fila vinculada para reagendamento.')
      return
    }
    let alive = true
    setDays(null)
    setDate('')
    setSlots(null)
    setSelectedSessionId(null)
    setError(null)
    getAvailableDays(serviceConfigId)
      .then((items) => {
        if (!alive) {return}
        const sorted = [...items].sort((a, b) => a.date.localeCompare(b.date))
        setDays(sorted)
        setDate(sorted[0]?.date ?? '')
      })
      .catch(() => {
        if (!alive) {return}
        setDays([])
        setError('Não foi possível carregar os dias disponíveis.')
      })
    return () => { alive = false }
  }, [serviceConfigId])

  useEffect(() => {
    if (!serviceConfigId || !date) {
      setSlots(null)
      return
    }
    let alive = true
    setSlots(null)
    setSelectedSessionId(null)
    setError(null)
    getDaySlots(serviceConfigId, date)
      .then((items) => {
        if (alive) {setSlots(items)}
      })
      .catch(() => {
        if (!alive) {return}
        setSlots([])
        setError('Não foi possível carregar as sessões deste dia.')
      })
    return () => { alive = false }
  }, [date, serviceConfigId])

  return (
    <Modal
      open
      onClose={submitting ? () => {} : onClose}
      titleId={titleId}
      title="Reagendar ticket"
      closeOnOverlay={!submitting}
    >
      <div data-screen-label="Reagendar ticket">
        <div className="fz-modal__icon"><Icon name="clock" size={24} /></div>
        <div className="fz-modal__title" id={titleId}>Reagendar ticket</div>
        <div className="fz-modal__text">
          Escolha uma nova sessão para <b>{ticket.codigoTicket}</b> continuar em {ticket.nomeServico}.
        </div>

        <div className="fz-field">
          <label className="fz-field__label">Dia disponível</label>
          <div className="fz-transfer__opts">
            {days === null && <div className="fz-sessioncal__empty">Carregando...</div>}
            {days?.length === 0 && <div className="fz-sessioncal__empty">Nenhum dia disponível para esta fila.</div>}
            {days?.map((day) => (
              <button
                key={day.date}
                className={'fz-transfer__opt ' + (date === day.date ? 'is-active' : '')}
                disabled={submitting}
                onClick={() => setDate(day.date)}
              >
                <span>{dateLabel(day.date)}</span>
                <span className="fz-transfer__sala">{day.availableCount} sessões disponíveis</span>
              </button>
            ))}
          </div>
        </div>

        {date && (
          <div className="fz-field">
            <label className="fz-field__label">Sessão</label>
            <div className="fz-transfer__opts">
              {slots === null && <div className="fz-sessioncal__empty">Carregando...</div>}
              {slots !== null && futureSlots.length === 0 && (
                <div className="fz-sessioncal__empty">
                  {availableSlots.length > 0 ? 'Os horários deste dia já passaram. Escolha outro dia.' : 'Sem sessões disponíveis neste dia.'}
                </div>
              )}
              {futureSlots.map((slot) => (
                <button
                  key={slot.sessionId}
                  className={'fz-transfer__opt ' + (selectedSessionId === slot.sessionId ? 'is-active' : '')}
                  disabled={submitting}
                  onClick={() => setSelectedSessionId(slot.sessionId)}
                >
                  <span className="fz-transfer__main">{sessionLabel(slot)}</span>
                  {slot.remainingCapacity != null && <span className="fz-transfer__sala">{slot.remainingCapacity} vagas restantes</span>}
                </button>
              ))}
              {pastSlots.length > 0 && (
                <div className="fz-transfer__pastdivider">Horários já passados</div>
              )}
              {pastSlots.map((slot) => (
                <button
                  key={slot.sessionId}
                  className="fz-transfer__opt is-past"
                  disabled
                  aria-disabled="true"
                  title="Horário já passou"
                >
                  <span className="fz-transfer__main">{sessionLabel(slot)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {error && <div className="fz-field__error" style={{ textAlign: 'left' }}>{error}</div>}

        <div className="fz-modal__actions">
          <button className="fz-btn fz-btn--neutral fz-btn--block" onClick={onClose} disabled={submitting}>Voltar</button>
          <button
            className="fz-btn fz-btn--primary fz-btn--block"
            disabled={selectedSessionId == null || submitting}
            aria-busy={submitting}
            onClick={() => selectedSessionId != null && !submitting && onConfirm(selectedSessionId)}
          >
            {submitting ? <><span className="fz-spin" /> Reagendando...</> : 'Reagendar'}
          </button>
        </div>
      </div>
    </Modal>
  )
}